import {
  COMPUTE_TASK_STATUS,
  COMPUTE_TASK_TYPES,
  VmLifecycleType,
} from '@/constants';
import { ComputeTaskType } from '@/gql/graphql';
import { useClipboard } from '@vueuse/core';
import { taskErrorMessage } from '@/utils/task-error-message';
import { BasespaceTransferItem } from '@/types/basespace-state';

export type DataPipelineTaskTabOptionType =
  | 'progress'
  | 'figures'
  | 'diagram'
  | 'files'
  | 'exec'
  | 'debug';

export const DATA_PIPELINE_TASK_TAB_OPTIONS = {
  PROGRESS: 'progress',
  FIGURES: 'figures',
  DIAGRAM: 'diagram',
  FILES: 'files',
  EXEC: 'exec',
  DEBUG: 'debug',
} as const;

export interface Attributes_Common {
  /** Behaviour of the worker VM after the task terminates */
  vmLifecycle?: VmLifecycleType;
  version?: string;
  machineType?: string;
  /** Optional but stronly recommended uuid v4 */
  gceRequestId?: string;
  [key: string]: unknown; // user-defined extras
}

export interface Attributes_FtpTransfer extends Attributes_Common {
  newBucketFriendlyName: string;
  newBucketDescription: string;
  ftpCmd: string;
  autoAnnotate: boolean;
  createProject: boolean;
  reconstructData: boolean;
  workflowLocation?: string;
  pipelineId?: number;
}

export interface Attributes_WasabiTransfer extends Attributes_Common {
  newBucketFriendlyName: string;
  newBucketDescription: string;
  wasabiTransfer: {
    sourceUri: string;
    endpointUrl: string;
    credentials: {
      accessKeyId: string;
      secretAccessKey: string;
    };
  };
  autoAnnotate: boolean;
  createProject: boolean;
  reconstructData: boolean;
  workflowLocation?: string;
  pipelineId?: number;
}
export interface Attributes_BaseSpaceTransfer extends Attributes_Common {
  newBucketFriendlyName: string;
  newBucketDescription: string;
  bsFilesList: BasespaceTransferItem[];
  createProject: boolean;
  reconstructData: boolean;
  workflowLocation?: string;
  pipelineId?: number;
}

export interface Attributes_NextFlowPipeline extends Attributes_Common {
  //
  // ALL PIPELINES - REQUIRED FIELDS
  //
  samples: {
    name: string | null;
    fileR1: string | null;
    fileR2: string | null;
    bucketInputName: string;
    barcodesWhitelist: string;
    reference: string;
    referenceTranscriptomeProfile: string;
    referenceOrganism?: string[] | null;
    umiLength?: number | null;
  }[];
  //
  // CATALOG PIPELINE
  //
  pipelineId?: number;
  //
  // CUSTOM PIPELINE
  //
  workflowLocation?: string;
}

export interface Attributes_BclToFastq_Sample {
  id: string;
  name: string;
  index: string;
  index2: string;
}

export interface Attributes_BclToFastq extends Attributes_Common {
  samples: Attributes_BclToFastq_Sample[];
  bucketId: number;
  bucketName: string;
  fileBcl: string;
}

export interface RawFtpFileProgressDetail {
  filename: string;
  status: string;
  progress: number;
  size: number;
  speed: number;
  avg_speed: number;
  elapsed: number;
  abort: boolean;
}

export interface FtpFileProgressDetail {
  filename: string;
  status: string;
  progress: number;
  size: number;
  speed: number;
  avgSpeed: number;
  elapsed: number;
  abort: boolean;
}

export interface ComputeTaskType_FtpTransfer extends ComputeTaskType {
  progress: number;
  isActive: boolean;
  canBeAborted: boolean;
  variant: string;
  fileProgressDetails: FtpFileProgressDetail[];
}

export class ComputeTaskUtils {
  public static progressVariant(taskStatus: string | undefined | null): string {
    if (
      taskStatus === COMPUTE_TASK_STATUS.CREATING ||
      taskStatus === COMPUTE_TASK_STATUS.PENDING
    ) {
      return 'info';
    } else if (taskStatus === COMPUTE_TASK_STATUS.RUNNING) {
      return 'primary';
    } else if (taskStatus === COMPUTE_TASK_STATUS.COMPLETED) {
      return 'success';
    } else if (
      taskStatus === COMPUTE_TASK_STATUS.STALLED ||
      taskStatus === COMPUTE_TASK_STATUS.STOPPED
    ) {
      return 'warning';
    }
    return 'danger';
  }

  public static canBeAborted(taskStatus: string | undefined | null): boolean {
    return (
      taskStatus === COMPUTE_TASK_STATUS.CREATING ||
      taskStatus === COMPUTE_TASK_STATUS.PENDING ||
      taskStatus === COMPUTE_TASK_STATUS.RUNNING
    );
  }

  public static progressActive(taskStatus: string | undefined | null): boolean {
    if (!taskStatus) return false;
    return (
      taskStatus === COMPUTE_TASK_STATUS.CREATING ||
      taskStatus === COMPUTE_TASK_STATUS.PENDING ||
      taskStatus === COMPUTE_TASK_STATUS.RUNNING ||
      taskStatus === COMPUTE_TASK_STATUS.ABORTING
    );
  }

  public static statusClass(
    status: string | undefined | null,
    bg = false,
    outline = false
  ): string {
    let taskStatusClass = '';
    switch (status) {
      case COMPUTE_TASK_STATUS.CREATING:
      case COMPUTE_TASK_STATUS.PENDING:
      case COMPUTE_TASK_STATUS.RUNNING:
        taskStatusClass = 'primary';
        break;
      case COMPUTE_TASK_STATUS.FAILED:
        taskStatusClass = 'danger';
        break;
      case COMPUTE_TASK_STATUS.ABORTING:
      case COMPUTE_TASK_STATUS.STOPPED:
      case COMPUTE_TASK_STATUS.STALLED:
        taskStatusClass = 'warning';
        break;
      case COMPUTE_TASK_STATUS.ABORTED:
        taskStatusClass = 'secondary';
        break;
      case COMPUTE_TASK_STATUS.COMPLETED:
        taskStatusClass = 'success';
        break;
      default:
        taskStatusClass = '';
    }
    if (bg) {
      taskStatusClass = 'bg-' + taskStatusClass;
    } else if (outline) {
      taskStatusClass = 'outline-' + taskStatusClass;
    }
    return taskStatusClass;
  }

  public static statusTitleClasses(
    status: string | undefined | null,
    bg = false,
    outline = false
  ): string[] {
    const taskStatusClass = this.statusClass(status, bg, outline);
    // If the status is warning, we want black text for better contrast.
    if (taskStatusClass.indexOf('warning') !== -1) {
      return ['text-black', taskStatusClass];
    }
    return ['text-white', 'accordion-title-text-white', taskStatusClass];
  }

  public static statusIcon(status: string | undefined | null): string {
    switch (status) {
      case COMPUTE_TASK_STATUS.CREATING:
      case COMPUTE_TASK_STATUS.PENDING:
      case COMPUTE_TASK_STATUS.RUNNING:
        return 'spinner';
      case COMPUTE_TASK_STATUS.FAILED:
        return 'exclamation-circle';
      case COMPUTE_TASK_STATUS.ABORTING:
        return 'exclamation-triangle';
      case COMPUTE_TASK_STATUS.ABORTED:
      case COMPUTE_TASK_STATUS.STOPPED:
      case COMPUTE_TASK_STATUS.STALLED:
        return 'circle-stop';
      case COMPUTE_TASK_STATUS.COMPLETED:
        return 'check-double';
      default:
        return 'list-check';
    }
  }

  public static statusIconClass(status: string | undefined | null): string[] {
    switch (status) {
      case COMPUTE_TASK_STATUS.CREATING:
      case COMPUTE_TASK_STATUS.PENDING:
      case COMPUTE_TASK_STATUS.RUNNING:
        return ['me-2', 'fa-spin'];
      case COMPUTE_TASK_STATUS.ABORTING:
        return ['me-2', 'fa-beat'];
      case COMPUTE_TASK_STATUS.STOPPED:
      case COMPUTE_TASK_STATUS.STALLED:
        return ['me-2', 'fa-pause'];
      default:
        return ['me-2'];
    }
  }

  public static progressTooltipHasContent(task: ComputeTaskType): boolean {
    // show tooltip if task is RUNNING
    if (task.status === COMPUTE_TASK_STATUS.RUNNING) {
      // show tooltip if BASESPACE transfer in progress
      if (task.taskType === COMPUTE_TASK_TYPES.BASESPACE_TRANSFER) {
        return this.fileProgressDetails_BasespaceTransfer(task).length > 0;
      }
      // show tooltip if FTP transfer in progress
      if (task.taskType === COMPUTE_TASK_TYPES.FTP_TRANSFER) {
        return this.fileProgressDetails_FtpTranfer(task).length > 0;
      }
      // otherwise show tooltip if RUNNING and task.results.progressDescription is not empty
      if (task.results && task.results.progressDescription) {
        return task.results.progressDescription.length > 0;
      }
    }

    // show tooltip if COMPLETED
    if (task.status === COMPUTE_TASK_STATUS.COMPLETED) {
      // show tooltip if BASESPACE transfer in progress
      if (task.taskType === COMPUTE_TASK_TYPES.BASESPACE_TRANSFER) {
        return this.fileProgressDetails_BasespaceTransfer(task).length > 0;
      }
      // show tooltip if FTP transfer in progress
      if (task.taskType === COMPUTE_TASK_TYPES.FTP_TRANSFER) {
        return this.fileProgressDetails_FtpTranfer(task).length > 0;
      }
    }

    // show error message if FAILED
    if (task.status === COMPUTE_TASK_STATUS.FAILED) {
      return taskErrorMessage(task.results).length > 0;
    }

    // otherwise do not show tooltip
    return false;
  }

  public static errorMessage(task: ComputeTaskType): string {
    return taskErrorMessage(task.results);
  }

  public static progressTooltipText(task: ComputeTaskType): string {
    return task.results.progressDescription || '';
  }

  public static progressPercentage(task: ComputeTaskType): number {
    // init
    let progress = NaN;
    // show progress text if task is running
    if (task.status === COMPUTE_TASK_STATUS.RUNNING) {
      // progressTotal is byte-weighted and authoritative for every task type, FTP included.
      // The falsy-zero gate is deliberate and unchanged: several task types publish a
      // placeholder 0 while running, and rendering "0%" for those is worse than falling
      // through to the status word. It also keeps FTP transfers that are still running the
      // old reporter — its code ships per task, so one can outlive this deploy — showing
      // their status rather than a hard 0%.
      if (task.results && task.results.progressTotal) {
        progress = Math.round(task.results.progressTotal);
      }
    }
    return progress;
  }

  public static progressText(task: ComputeTaskType): string | undefined | null {
    // show progress percentage text if task is running
    const progress = this.progressPercentage(task);

    // check if we have a valid percentage
    if (!isNaN(progress)) {
      return `${progress}%`;
    }

    // otherwise
    return task.status;
  }

  public static fileProgressDetails_BasespaceTransfer(
    task: ComputeTaskType
  ): BasespaceTransferItem[] {
    return task.results && Array.isArray(task.results.bsFilesProgress)
      ? task.results.bsFilesProgress
      : [];
  }

  public static fileProgressDetails_FtpTranfer(
    task: ComputeTaskType
  ): FtpFileProgressDetail[] {
    return task.results && Array.isArray(task.results.files)
      ? task.results.files.map((file: unknown) => {
          const rawFile = file as RawFtpFileProgressDetail;
          return {
            filename: rawFile.filename,
            status: rawFile.status,
            progress: Number(rawFile.progress),
            size: Number(rawFile.size),
            speed: Number(rawFile.speed),
            avgSpeed: Number(rawFile.avg_speed),
            elapsed: Number(rawFile.elapsed),
            abort: Boolean(rawFile.abort),
          } as FtpFileProgressDetail;
        })
      : [];
  }

  public static debugDetails(task: ComputeTaskType): string {
    // stringify task object for debugging
    return JSON.stringify(task, null, 2);
  }

  public static copyDebugDetails(task: ComputeTaskType): void {
    const clipboard = useClipboard();
    clipboard.copy(this.debugDetails(task));
  }
}
